import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, Building2, DollarSign, Target, FileText,
  CheckCircle, XCircle, AlertTriangle, Loader2, Sparkles,
  RotateCcw, Send, Edit3, Save, X, Plus, Clock,
  ThumbsUp, ThumbsDown, MessageSquare, Download, Upload,
  Link2, Trash2, File, FileSpreadsheet, FileText as FileDoc,
  Mail, ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrency as fmtCur } from '@/lib/market';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  draft:              'bg-[#f5f5f5] text-[#4a4a4a]',
  submitted:          'bg-[#d9e2c3] text-[#1a1a1a]',
  under_review:       'bg-amber-100 text-amber-700',
  approved:           'bg-emerald-100 text-emerald-700',
  rejected:           'bg-red-100 text-red-600',
  revision_requested: 'bg-orange-100 text-orange-700',
};

const MEMO_SECTIONS = [
  { key: 'executive_summary',    label: 'Executive Summary' },
  { key: 'investment_thesis',    label: 'Investment Thesis' },
  { key: 'company_overview',     label: 'Company Overview' },
  { key: 'market_analysis',      label: 'Market Analysis' },
  { key: 'competitive_landscape', label: 'Competitive Landscape' },
  { key: 'financials',           label: 'Financials' },
  { key: 'team_assessment',      label: 'Team Assessment' },
  { key: 'risks_and_mitigants',  label: 'Risks & Mitigants' },
  { key: 'deal_terms',           label: 'Deal Terms' },
  { key: 'recommendation',       label: 'Recommendation' },
];

const DOC_TYPES = [
  { value: 'pitch_deck',      label: 'Pitch Deck' },
  { value: 'financial_model', label: 'Financial Model / MIS' },
  { value: 'term_sheet',      label: 'Term Sheet' },
  { value: 'data_room',       label: 'Data Room' },
  { value: 'legal_docs',      label: 'Legal Documents' },
  { value: 'meeting_notes',   label: 'Meeting Notes' },
  { value: 'reference_check', label: 'Reference Check' },
  { value: 'other',           label: 'Other' },
];

const ACCEPT_TYPES = '.pdf,.xlsx,.xls,.docx,.doc,.csv';

const DONUT_COLORS = ['#ffd87a', '#e8a838', '#c97a3a', '#dae4c4', '#c8d9b0', '#a86830', '#eae9da', '#d9d9d9'];
const HOLDER_TYPE_LABELS = { founder: 'Founder', investor: 'Investor', employee: 'Employee', option_pool: 'Option Pool' };
const SECURITY_TYPE_LABELS = { common: 'Common', preferred: 'Preferred', options: 'Options', warrants: 'Warrants', safe: 'SAFE', convertible: 'Convertible' };
const INVESTMENT_TYPE_LABELS = { equity: 'Equity', safe: 'SAFE', convertible_note: 'Convertible Note', warrant: 'Warrant', secondary: 'Secondary' };

const getErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return err?.message || fallback;
};

function emptyCapTableRow() {
  return { holder_name: '', holder_type: 'investor', security_type: 'common', shares: '', ownership_percentage: '', fully_diluted_percentage: '', notes: '' };
}

// ── Donut Chart ───────────────────────────────────────────────────────────
const DonutChart = ({ data, size = 170 }) => {
  const [hovered, setHovered] = useState(null);
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total <= 0) return null;
  let angle = -90;
  const r = size / 2 - 12, cx = size / 2, cy = size / 2, ir = r * 0.52;
  if (data.length === 1) {
    return (
      <svg width={size} height={size}>
        <circle cx={cx} cy={cy} r={r} fill={data[0].color} />
        <circle cx={cx} cy={cy} r={ir} fill="white" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} onMouseLeave={() => setHovered(null)}>
      {data.map((item, i) => {
        const pct = item.value / total, deg = pct * 360;
        const s = angle, e2 = angle + deg; angle = e2;
        const sr = s * Math.PI / 180, er = e2 * Math.PI / 180;
        const d = [
          `M ${cx + r * Math.cos(sr)} ${cy + r * Math.sin(sr)}`,
          `A ${r} ${r} 0 ${deg > 180 ? 1 : 0} 1 ${cx + r * Math.cos(er)} ${cy + r * Math.sin(er)}`,
          `L ${cx + ir * Math.cos(er)} ${cy + ir * Math.sin(er)}`,
          `A ${ir} ${ir} 0 ${deg > 180 ? 1 : 0} 0 ${cx + ir * Math.cos(sr)} ${cy + ir * Math.sin(sr)}`,
          'Z',
        ].join(' ');
        return <path key={i} d={d} fill={item.color} stroke="none"
          style={{ opacity: hovered == null || hovered === i ? 1 : 0.35, transition: 'opacity 0.15s' }}
          onMouseEnter={() => setHovered(i)} />;
      })}
    </svg>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────

const parseDealTermsObj = (text) => {
  if (!text) return null;
  try {
    const p = JSON.parse(text);
    if (typeof p === 'object' && !Array.isArray(p)) return p;
  } catch {}
  const entries = {};
  text.split('\n').forEach(line => {
    const idx = line.indexOf(':');
    if (idx > 0) {
      const k = line.slice(0, idx).trim().replace(/^[*\-\s]+/, '');
      const v = line.slice(idx + 1).trim();
      if (k && v) entries[k] = v;
    }
  });
  return Object.keys(entries).length > 0 ? entries : null;
};

const parseChartData = (text) => {
  if (!text) return null;
  const data = [];
  const seen = new Set();
  text.split('\n').forEach(line => {
    const m = line.match(
      /^[\s*\-\d.]*([A-Za-z][A-Za-z\s\/\-()]+?)[\s:]+\$?([\d,]+(?:\.\d+)?)\s*([BbMmKkTt%]?(?:illion|rillion)?)/
    );
    if (!m) return;
    const label = m[1].trim().replace(/\*\*/g, '').replace(/\s+/g, ' ').slice(0, 28);
    if (seen.has(label)) return;
    let num = parseFloat(m[2].replace(/,/g, ''));
    const sfx = (m[3] || '').toLowerCase();
    if (sfx.startsWith('b') || sfx.includes('billion')) num *= 1e9;
    else if (sfx.startsWith('t') || sfx.includes('trillion')) num *= 1e12;
    else if (sfx.startsWith('m') && !sfx.includes('%')) num *= 1e6;
    else if (sfx.startsWith('k')) num *= 1e3;
    if (!isNaN(num) && num > 0 && num < 1e16) {
      data.push({ name: label, value: num, raw: m[2] + (m[3] || '') });
      seen.add(label);
    }
  });
  return data.length >= 3 ? data.slice(0, 8) : null;
};

const fmtNum = (v) => {
  if (v >= 1e12) return `${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9)  return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6)  return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3)  return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(1);
};

const docIcon = (filename = '') => {
  const ext = filename.split('.').pop().toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) return FileSpreadsheet;
  if (['docx', 'doc'].includes(ext)) return FileDoc;
  return File;
};

// ── Section content renderer ───────────────────────────────────────────────

function SectionContent({ sectionKey, value }) {
  if (!value) return <span className="text-[#4a4a4a] italic text-sm">No content yet</span>;

  if (sectionKey === 'deal_terms') {
    const obj = parseDealTermsObj(value);
    if (obj) {
      return (
        <div className="overflow-hidden rounded-lg border border-[#e5e5e5]">
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(obj).map(([k, v], i) => (
                <tr key={k} className={i % 2 === 0 ? 'bg-[#f9f9f9]' : 'bg-white'}>
                  <td className="px-4 py-2.5 font-medium text-[#2a2a2a] w-48 border-r border-[#e5e5e5]">{k}</td>
                  <td className="px-4 py-2.5 text-[#4a4a4a]">{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
  }

  const CHART_SECTIONS = ['financials', 'market_analysis'];
  if (CHART_SECTIONS.includes(sectionKey)) {
    const chartData = parseChartData(value);
    return (
      <div>
        <div className="text-sm text-[#4a4a4a] leading-relaxed mb-4 prose prose-sm max-w-none [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:pl-5 [&_ol]:list-decimal [&_li]:my-0.5 [&_strong]:text-[#1a1a1a] [&_p]:my-1">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
        </div>
        {chartData && (
          <div className="rounded-lg border border-[#e5e5e5] bg-[#f9f9f9] p-4">
            <p className="text-xs font-medium text-[#4a4a4a] mb-2">Key Metrics</p>
            <ReactECharts
              option={{
                tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}<br/><b>${fmtNum(p[0].value)}</b>` },
                grid: { left: 10, right: 10, top: 10, bottom: 60, containLabel: true },
                xAxis: {
                  type: 'category', data: chartData.map(d => d.name),
                  axisLabel: { rotate: 30, fontSize: 10, color: '#64748b', interval: 0 },
                  axisLine: { lineStyle: { color: '#e2e8f0' } },
                },
                yAxis: {
                  type: 'value',
                  axisLabel: { formatter: fmtNum, fontSize: 10, color: '#64748b' },
                  splitLine: { lineStyle: { color: '#f1f5f9' } },
                },
                series: [{
                  type: 'bar', data: chartData.map(d => d.value),
                  itemStyle: {
                    color: (p) => ['#ffd87a','#e8a838','#c97a3a','#a86830','#d4956a','#d9c4a0','#b8a070','#8ba888'][p.dataIndex % 8],
                    borderRadius: [4, 4, 0, 0],
                  },
                  barMaxWidth: 48,
                }],
              }}
              style={{ height: 200 }}
              opts={{ renderer: 'svg' }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="text-sm text-[#4a4a4a] leading-relaxed min-h-[240px] prose prose-sm max-w-none break-words [overflow-wrap:break-word] [word-break:break-word]
      [&_p]:text-[#4a4a4a] [&_p]:my-1.5
      [&_ul]:text-[#4a4a4a] [&_ul]:my-1.5 [&_ul]:pl-5 [&_ul]:list-disc
      [&_ol]:text-[#4a4a4a] [&_ol]:my-1.5 [&_ol]:pl-5 [&_ol]:list-decimal
      [&_li]:text-[#4a4a4a] [&_li]:my-0.5
      [&_strong]:text-[#1a1a1a] [&_strong]:font-semibold
      [&_h1]:text-[#1a1a1a] [&_h2]:text-[#1a1a1a] [&_h3]:text-[#1a1a1a]
      [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold
      [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm
      [&_blockquote]:border-l-2 [&_blockquote]:border-[#e5e5e5] [&_blockquote]:pl-3 [&_blockquote]:text-[#4a4a4a]
      [&_table]:w-full [&_table]:text-xs [&_th]:text-left [&_th]:p-2 [&_th]:border-b [&_th]:border-[#e5e5e5] [&_td]:p-2 [&_td]:border-b [&_td]:border-[#e5e5e5]">
      <div className="overflow-x-auto">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function DealDetailPage() {
  const { user, hasPermission } = useAuth();
  const { dealStages } = useAppData();
  const { dealId } = useParams();

  const [deal, setDeal] = useState(null);
  const [icMemo, setIcMemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [addingLink, setAddingLink] = useState(false);
  const [linkForm, setLinkForm] = useState({ title: '', url: '', type: 'other' });

  // Per-section editing
  const [editingSections, setEditingSections] = useState(new Set());
  const [sectionDrafts, setSectionDrafts] = useState({});
  const [savingSection, setSavingSection] = useState(null);

  // IC Memo header actions
  const [generatingAI, setGeneratingAI] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [activeSection, setActiveSection] = useState('executive_summary');

  // Reject / revision dialogs (reviewer actions)
  const [rejectDialog, setRejectDialog] = useState(false);
  const [revisionDialog, setRevisionDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [revisionComments, setRevisionComments] = useState('');

  // Reviewer selection on submit
  const [reviewerDialog, setReviewerDialog] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const [selectedReviewerId, setSelectedReviewerId] = useState('');
  const [loadingReviewers, setLoadingReviewers] = useState(false);
  const [submittingMemo, setSubmittingMemo] = useState(false);

  // IC Summary + send to external IC
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [sendICDialog, setSendICDialog] = useState(false);
  const [sendRecipients, setSendRecipients] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sendingIC, setSendingIC] = useState(false);

  // Create memo
  const [creatingMemo, setCreatingMemo] = useState(false);

  // Cap Table
  const [investments, setInvestments] = useState([]);
  const [capTableHistory, setCapTableHistory] = useState([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);
  const [snapshotDialog, setSnapshotDialog] = useState(false);
  const [snapshotForm, setSnapshotForm] = useState({ snapshot_date: '', total_shares_outstanding: '', fully_diluted_shares: '', entries: [] });
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [safeDialog, setSafeDialog] = useState(false);
  const [safeForm, setSafeForm] = useState({ round_price_per_share: '', pre_money_valuation: '', fully_diluted_shares_pre_round: '' });
  const [convertingSafes, setConvertingSafes] = useState(false);
  const [waterfallDialog, setWaterfallDialog] = useState(false);
  const [exitValue, setExitValue] = useState('');
  const [waterfallResult, setWaterfallResult] = useState(null);
  const [runningWaterfall, setRunningWaterfall] = useState(false);

  const canEdit   = hasPermission('deals:update');
  const canApprove = hasPermission('deals:approve');

  const formatCurrency = (v) => (!v ? '—' : fmtCur(v, user?.market));

  const fetchDealData = useCallback(async () => {
    try {
      const dealRes = await axios.get(`${API}/deals/${dealId}`);
      setDeal(dealRes.data);
      try {
        const memoRes = await axios.get(`${API}/deals/${dealId}/ic-memo`);
        setIcMemo(memoRes.data);
      } catch {
        setIcMemo(null);
      }
      try {
        const docsRes = await axios.get(`${API}/deals/${dealId}/documents`);
        setDocuments(docsRes.data || []);
      } catch {
        setDocuments([]);
      }
      try {
        const invRes = await axios.get(`${API}/deals/${dealId}/investments`);
        setInvestments(invRes.data || []);
      } catch {
        setInvestments([]);
      }
      try {
        const historyRes = await axios.get(`${API}/deals/${dealId}/cap-table/history`);
        const history = historyRes.data || [];
        setCapTableHistory(history);
        const current = history.find(s => s.is_current) || history[0];
        setSelectedSnapshotId(current?.id || null);
      } catch {
        setCapTableHistory([]);
      }
    } catch {
      toast.error('Failed to load deal');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { fetchDealData(); }, [fetchDealData]);

  // ── Cap Table actions ──────────────────────────────

  const selectedSnapshot = capTableHistory.find(s => s.id === selectedSnapshotId) || null;
  const capTableEntries = selectedSnapshot?.entries || [];

  const openSnapshotDialog = () => {
    setSnapshotForm({
      snapshot_date: new Date().toISOString().slice(0, 10),
      total_shares_outstanding: selectedSnapshot?.total_shares_outstanding || '',
      fully_diluted_shares: selectedSnapshot?.fully_diluted_shares || '',
      entries: capTableEntries.length ? capTableEntries.map(e => ({ ...e })) : [emptyCapTableRow()],
    });
    setSnapshotDialog(true);
  };

  const updateCapTableRow = (idx, field, value) => {
    setSnapshotForm(f => ({ ...f, entries: f.entries.map((row, i) => i === idx ? { ...row, [field]: value } : row) }));
  };
  const addCapTableRow = () => setSnapshotForm(f => ({ ...f, entries: [...f.entries, emptyCapTableRow()] }));
  const removeCapTableRow = (idx) => setSnapshotForm(f => ({ ...f, entries: f.entries.filter((_, i) => i !== idx) }));

  const submitSnapshot = async () => {
    setSavingSnapshot(true);
    try {
      const payload = {
        deal_id: dealId,
        snapshot_date: snapshotForm.snapshot_date,
        total_shares_outstanding: parseFloat(snapshotForm.total_shares_outstanding) || 0,
        fully_diluted_shares: parseFloat(snapshotForm.fully_diluted_shares) || 0,
        entries: snapshotForm.entries.map(row => ({
          holder_name: row.holder_name,
          holder_type: row.holder_type,
          security_type: row.security_type,
          shares: parseFloat(row.shares) || 0,
          ownership_percentage: parseFloat(row.ownership_percentage) || 0,
          fully_diluted_percentage: parseFloat(row.fully_diluted_percentage) || 0,
          notes: row.notes || null,
        })),
      };
      await axios.post(`${API}/deals/cap-table`, payload);
      toast.success('Cap table snapshot saved');
      setSnapshotDialog(false);
      await fetchDealData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save snapshot'));
    } finally {
      setSavingSnapshot(false);
    }
  };

  const runConvertSafes = async () => {
    setConvertingSafes(true);
    try {
      const res = await axios.post(`${API}/deals/${dealId}/cap-table/convert-safes`, {
        round_price_per_share: parseFloat(safeForm.round_price_per_share) || 0,
        pre_money_valuation: parseFloat(safeForm.pre_money_valuation) || 0,
        fully_diluted_shares_pre_round: parseFloat(safeForm.fully_diluted_shares_pre_round) || 0,
      });
      const conversions = res.data?.conversions || [];
      if (conversions.length === 0) {
        toast.info('No active SAFE / convertible-note investments to convert');
      } else {
        setSnapshotForm(f => ({
          ...f,
          entries: [
            ...f.entries.filter(r => r.holder_name),
            ...conversions.map(c => ({
              holder_name: c.entity_id || '',
              holder_type: 'investor',
              security_type: 'convertible',
              shares: c.converted_shares?.toFixed(2) || '',
              ownership_percentage: '',
              fully_diluted_percentage: '',
              notes: `Converted @ $${c.effective_price_per_share?.toFixed(2)}/sh`,
            })),
          ],
        }));
        toast.success(`Converted ${conversions.length} SAFE/note position(s) — review rows in the snapshot form`);
      }
      setSafeDialog(false);
      if (!snapshotDialog) openSnapshotDialog();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to convert SAFEs'));
    } finally {
      setConvertingSafes(false);
    }
  };

  const runWaterfall = async () => {
    if (!exitValue || isNaN(parseFloat(exitValue))) {
      toast.error('Enter an exit value first');
      return;
    }
    if (!selectedSnapshot) {
      toast.error('No cap table snapshot exists yet for this deal');
      return;
    }
    setRunningWaterfall(true);
    setWaterfallResult(null);
    try {
      const res = await axios.post(`${API}/deals/${dealId}/cap-table/waterfall`, { exit_value: parseFloat(exitValue) });
      setWaterfallResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to run waterfall'));
    } finally {
      setRunningWaterfall(false);
    }
  };

  // ── Deal actions ──────────────────────────────────

  const handleAdvance = async () => {
    setAdvancing(true);
    try {
      await axios.post(`${API}/deals/${dealId}/advance`);
      toast.success('Deal advanced');
      fetchDealData();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to advance deal');
    } finally {
      setAdvancing(false);
    }
  };

  const handlePass = async () => {
    const reason = prompt('Reason for passing on this deal:');
    if (!reason) return;
    try {
      await axios.post(`${API}/deals/${dealId}/pass?reason=${encodeURIComponent(reason)}`);
      toast.success('Deal marked as passed');
      fetchDealData();
    } catch {
      toast.error('Failed to pass deal');
    }
  };

  // ── Document actions ──────────────────────────────

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('deal_id', dealId);
      fd.append('title', file.name);
      fd.append('document_type', 'other');
      const res = await axios.post(`${API}/deals/documents/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocuments(prev => [res.data, ...prev]);
      toast.success('File uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleAddLink = async () => {
    if (!linkForm.url || !linkForm.title) { toast.error('Title and URL required'); return; }
    try {
      const res = await axios.post(`${API}/deals/documents`, {
        deal_id: dealId,
        document_type: linkForm.type,
        title: linkForm.title,
        file_url: linkForm.url,
        file_name: linkForm.url,
      });
      setDocuments(prev => [res.data, ...prev]);
      setAddingLink(false);
      setLinkForm({ title: '', url: '', type: 'other' });
      toast.success('Link added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add link');
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/deals/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document removed');
    } catch {
      toast.error('Failed to remove document');
    }
  };

  // ── IC Memo creation ──────────────────────────────

  const handleCreateMemo = async () => {
    setCreatingMemo(true);
    try {
      const res = await axios.post(`${API}/deals/ic-memos`, {
        deal_id: dealId,
        title: `IC Memo — ${deal.deal_name}`,
      });
      setIcMemo(res.data);
      toast.success('IC Memo created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create memo');
    } finally {
      setCreatingMemo(false);
    }
  };

  // ── Per-section editing ───────────────────────────

  const startEdit = (key) => {
    setSectionDrafts(prev => ({ ...prev, [key]: icMemo[key] || '' }));
    setEditingSections(prev => new Set([...prev, key]));
  };

  const cancelEdit = (key) => {
    setEditingSections(prev => { const s = new Set(prev); s.delete(key); return s; });
    setSectionDrafts(prev => { const d = { ...prev }; delete d[key]; return d; });
  };

  const saveSection = async (key) => {
    setSavingSection(key);
    try {
      const res = await axios.put(`${API}/deals/ic-memos/${icMemo.id}`, { [key]: sectionDrafts[key] });
      setIcMemo(res.data);
      cancelEdit(key);
      toast.success('Section saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSection(null);
    }
  };

  // ── IC Memo actions ───────────────────────────────

  const handleGenerateAI = async () => {
    if (!icMemo) return;
    setGeneratingAI(true);
    try {
      const res = await axios.post(`${API}/deals/ic-memos/${icMemo.id}/generate-ai`);
      setIcMemo(res.data);
      toast.success('IC Memo generated with live research data');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'AI generation failed');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!icMemo) return;
    setDownloadingPDF(true);
    try {
      const res = await axios.get(`${API}/deals/ic-memos/${icMemo.id}/report`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IC_Memo_${(deal?.deal_name || 'memo').replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(`Failed to download PDF: ${err?.response?.data?.detail || err?.message}`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const openReviewerDialog = async () => {
    setReviewerDialog(true);
    setLoadingReviewers(true);
    try {
      const res = await axios.get(`${API}/deals/ic-memos/reviewers`);
      setReviewers(res.data || []);
      if (res.data?.length > 0) setSelectedReviewerId(res.data[0].id);
    } catch {
      toast.error('Failed to load reviewers');
    } finally {
      setLoadingReviewers(false);
    }
  };

  const handleSubmitMemo = async () => {
    if (!icMemo) return;
    setSubmittingMemo(true);
    try {
      const res = await axios.post(`${API}/deals/ic-memos/${icMemo.id}/submit`, {
        reviewer_id: selectedReviewerId || null,
      });
      setIcMemo(res.data);
      setReviewerDialog(false);
      toast.success('Memo submitted for review');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to submit');
    } finally {
      setSubmittingMemo(false);
    }
  };

  const handleApproveMemo = async () => {
    if (!icMemo) return;
    try {
      const res = await axios.post(`${API}/deals/ic-memos/${icMemo.id}/approve`);
      setIcMemo(res.data);
      toast.success('Memo approved');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to approve');
    }
  };

  const handleRejectMemo = async () => {
    if (!icMemo) return;
    try {
      const res = await axios.post(`${API}/deals/ic-memos/${icMemo.id}/reject`, { reason: rejectReason });
      setIcMemo(res.data);
      setRejectDialog(false);
      setRejectReason('');
      toast.success('Memo rejected');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to reject');
    }
  };

  const handleRequestRevision = async () => {
    if (!icMemo) return;
    try {
      const res = await axios.post(`${API}/deals/ic-memos/${icMemo.id}/request-revision`, { comments: revisionComments });
      setIcMemo(res.data);
      setRevisionDialog(false);
      setRevisionComments('');
      toast.success('Revision requested');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to request revision');
    }
  };

  const handleGenerateSummary = async () => {
    if (!icMemo) return;
    setGeneratingSummary(true);
    try {
      const res = await axios.post(`${API}/deals/ic-memos/${icMemo.id}/generate-summary`);
      setIcMemo(res.data);
      setActiveSection('ic_summary');
      toast.success('IC Summary generated');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleSendToExternalIC = async () => {
    const recipientList = sendRecipients.split(',').map(r => r.trim()).filter(Boolean);
    if (!recipientList.length) { toast.error('Enter at least one email address'); return; }
    setSendingIC(true);
    try {
      const res = await axios.post(`${API}/deals/ic-memos/${icMemo.id}/send-to-external-ic`, {
        recipients: recipientList,
        subject: sendSubject || undefined,
        message: sendMessage || undefined,
      });
      setIcMemo(res.data);
      setSendICDialog(false);
      setSendRecipients('');
      setSendSubject('');
      setSendMessage('');
      toast.success(`Sent to ${recipientList.length} recipient(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to send');
    } finally {
      setSendingIC(false);
    }
  };

  // ── Stage helpers ─────────────────────────────────

  const getStageInfo = (stageId) => {
    const s = dealStages.find(s => s.key === stageId);
    return { label: s?.label || stageId, color: s?.data?.color || 'bg-gray-400' };
  };

  const getStageIndex = (stageId) => dealStages.findIndex(s => s.key === stageId);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-[#ffd87a]" />
      </div>
    );
  }

  if (!deal) return <div className="p-6 text-[#4a4a4a]">Deal not found</div>;

  const stageInfo = getStageInfo(deal.stage);
  const stageIndex = getStageIndex(deal.stage);
  const isFinalStage = ['closed', 'passed', 'lost'].includes(deal.stage);
  const memoStatus = icMemo?.status;

  // Who can do what on the IC memo
  const analystCanEdit   = canEdit    && memoStatus && ['draft', 'revision_requested', 'submitted'].includes(memoStatus);
  const reviewerCanEdit  = canApprove && memoStatus && ['submitted', 'under_review'].includes(memoStatus);
  const canEditMemo      = analystCanEdit || reviewerCanEdit;
  const canSubmitMemo    = canEdit    && memoStatus && ['draft', 'revision_requested', 'submitted'].includes(memoStatus);
  const canReviewMemo    = canApprove && memoStatus && ['submitted', 'under_review'].includes(memoStatus);
  const isApproved       = memoStatus === 'approved';

  // Nav sections: standard + summary if generated
  const navSections = [
    ...MEMO_SECTIONS,
    ...(icMemo?.ic_summary ? [{ key: 'ic_summary', label: 'IC Summary' }] : []),
  ];

  return (
    <div className="p-6" data-testid="deal-detail-page">

      {/* Header */}
      <div className="mb-6">
        <Link to="/private-markets?tab=startups" className="inline-flex items-center gap-2 text-[#4a4a4a] hover:text-[#1a1a1a] mb-4 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Deals
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[#1a1a1a]">{deal.deal_name}</h1>
              <span className={`px-3 py-1 rounded-full text-sm ${stageInfo.color} text-white`}>
                {stageInfo.label}
              </span>
            </div>
            <p className="text-[#4a4a4a] mt-1">{deal.company_name}</p>
          </div>
          {!isFinalStage && canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={handlePass}>
                <XCircle className="w-4 h-4 mr-2" /> Pass
              </Button>
              <Button className="bg-[#ffd87a] hover:bg-[#ffd87a]" onClick={handleAdvance} disabled={advancing}>
                {advancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Advance Deal
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Stage Progress */}
      <Card className="bg-white border-[#e5e5e5] mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            {dealStages.slice(0, 8).map((stage, idx) => {
              const isActive = stage.key === deal.stage;
              const isPast   = idx < stageIndex;
              return (
                <React.Fragment key={stage.key}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                      isActive ? `${stage.data?.color || 'bg-gray-400'} text-white`
                      : isPast ? 'bg-[#ffd87a] text-white'
                      : 'bg-[#e5e5e5] text-[#4a4a4a]'
                    }`}>
                      {isPast ? '✓' : idx + 1}
                    </div>
                    <p className={`text-xs mt-2 ${isActive ? 'text-[#1a1a1a] font-medium' : 'text-[#4a4a4a]'}`}>
                      {stage.label}
                    </p>
                  </div>
                  {idx < 7 && <div className={`flex-1 h-0.5 mx-2 ${idx < stageIndex ? 'bg-[#ffd87a]' : 'bg-[#e5e5e5]'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Deal Info Grid */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        <Card className="bg-white border-[#e5e5e5]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-[#1a1a1a]">
              <DollarSign className="w-4 h-4 text-[#ffd87a]" /> Investment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {[
              ['Target Amount', formatCurrency(deal.target_investment_amount)],
              ['Target Ownership', deal.target_ownership ? `${deal.target_ownership}%` : '—'],
              ['Pre-Money Val.', formatCurrency(deal.pre_money_valuation)],
              ['Round Size', formatCurrency(deal.round_size)],
            ].map(([label, val]) => (
              <div key={label} className="flex justify-between">
                <span className="text-[#4a4a4a]">{label}</span>
                <span className="font-medium text-[#1a1a1a]">{val}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white border-[#e5e5e5]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-[#1a1a1a]">
              <Building2 className="w-4 h-4 text-[#ffd87a]" /> Deal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {[
              ['Sector', deal.sector || '—'],
              ['Source', deal.source?.replace(/_/g, ' ') || '—'],
              ['Priority', deal.priority],
              ['Target Close', deal.target_close_date || '—'],
            ].map(([label, val]) => (
              <div key={label} className="flex justify-between">
                <span className="text-[#4a4a4a]">{label}</span>
                <span className="text-[#1a1a1a] capitalize">{val}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white border-[#e5e5e5]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-[#1a1a1a]">
              <Target className="w-4 h-4 text-[#ffd87a]" /> Thesis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#4a4a4a] mb-4">{deal.thesis || 'No thesis provided'}</p>
            {deal.key_risks?.length > 0 && (
              <div>
                <p className="text-xs text-[#4a4a4a] mb-2 font-medium">Key Risks</p>
                <div className="space-y-1">
                  {deal.key_risks.map((risk, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                      <span className="text-[#4a4a4a]">{risk}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Cap Table Section ─────────────────────────────────────────── */}
      <Card className="bg-white border-[#e5e5e5] mb-6">
        <CardHeader className="pb-3 border-b border-[#e5e5e5]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2 text-[#1a1a1a]">
              <DollarSign className="w-4 h-4 text-[#ffd87a]" /> Cap Table
            </CardTitle>
            <div className="flex items-center gap-2">
              {capTableHistory.length > 0 && (
                <select
                  value={selectedSnapshotId || ''}
                  onChange={e => setSelectedSnapshotId(e.target.value)}
                  className="text-xs border border-[#e5e5e5] rounded-lg pl-3 pr-8 py-1.5 bg-white min-w-[190px] appearance-none bg-no-repeat bg-[length:14px]"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%234a4a4a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 8px center',
                  }}
                >
                  {capTableHistory.map(s => (
                    <option key={s.id} value={s.id}>{s.snapshot_date}{s.is_current ? ' (current)' : ''}</option>
                  ))}
                </select>
              )}
              <Button variant="outline" size="sm" onClick={() => setWaterfallDialog(true)} className="text-xs">
                Run Waterfall
              </Button>
              <Button size="sm" onClick={openSnapshotDialog} className="text-xs bg-[#ffd87a] hover:bg-[#ffd87a]">
                <Plus className="w-3.5 h-3.5 mr-1" /> New Snapshot
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {investments.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-medium text-[#4a4a4a] uppercase tracking-wider mb-2">Investment Rounds</p>
              <div className="border border-[#e5e5e5] overflow-hidden overflow-x-auto rounded-lg">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#dae4c4]">
                      {['Round', 'Type', 'Security', 'Amount', 'Terms'].map(h => (
                        <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-[#1a1a1a] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e5e5e5]">
                    {investments.map((inv, idx) => (
                      <tr key={inv.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
                        <td className="px-3 py-2 text-xs text-[#1a1a1a]">{inv.round_name}</td>
                        <td className="px-3 py-2 text-xs text-[#4a4a4a]">{INVESTMENT_TYPE_LABELS[inv.investment_type] || inv.investment_type}</td>
                        <td className="px-3 py-2 text-xs text-[#4a4a4a]">{SECURITY_TYPE_LABELS[inv.security_type] || inv.security_type}</td>
                        <td className="px-3 py-2 text-xs text-[#1a1a1a]">{fmtCur(inv.investment_amount, user?.market)}</td>
                        <td className="px-3 py-2 text-[10px] text-[#4a4a4a]">
                          {inv.valuation_cap ? `Cap ${fmtCur(inv.valuation_cap, user?.market)}` : ''}
                          {inv.discount_rate ? ` · ${(inv.discount_rate * 100).toFixed(0)}% disc.` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!selectedSnapshot ? (
            <div className="text-center py-10 border border-dashed border-[#e5e5e5] rounded-lg">
              <p className="text-sm text-[#4a4a4a]">No cap table snapshot yet — click "New Snapshot" to add one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-1 flex flex-col items-center gap-3">
                <DonutChart
                  data={Object.entries(
                    capTableEntries.reduce((acc, e) => {
                      const key = e.holder_type || 'other';
                      acc[key] = (acc[key] || 0) + (e.shares || 0);
                      return acc;
                    }, {})
                  ).map(([type, value], i) => ({ name: HOLDER_TYPE_LABELS[type] || type, value, color: DONUT_COLORS[i % DONUT_COLORS.length] }))}
                />
                <div className="w-full space-y-1">
                  {Object.entries(
                    capTableEntries.reduce((acc, e) => {
                      const key = e.holder_type || 'other';
                      acc[key] = (acc[key] || 0) + (e.shares || 0);
                      return acc;
                    }, {})
                  ).map(([type, value], i) => (
                    <div key={type} className="flex items-center gap-2 text-xs">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                      <span className="flex-1 text-[#4a4a4a]">{HOLDER_TYPE_LABELS[type] || type}</span>
                      <span className="font-medium text-[#1a1a1a]">{value.toLocaleString()} sh</span>
                    </div>
                  ))}
                </div>
                <div className="w-full pt-2 border-t border-[#e5e5e5] text-xs text-[#4a4a4a] flex justify-between">
                  <span>Total O/S</span><span className="font-medium text-[#1a1a1a]">{selectedSnapshot.total_shares_outstanding?.toLocaleString()}</span>
                </div>
                <div className="w-full text-xs text-[#4a4a4a] flex justify-between">
                  <span>Fully Diluted</span><span className="font-medium text-[#1a1a1a]">{selectedSnapshot.fully_diluted_shares?.toLocaleString()}</span>
                </div>
              </div>
              <div className="lg:col-span-2 border border-[#e5e5e5] overflow-hidden overflow-x-auto rounded-lg">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#dae4c4]">
                      {['Holder', 'Type', 'Security', 'Shares', 'Own. %', 'FD %'].map(h => (
                        <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-[#1a1a1a] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e5e5e5]">
                    {capTableEntries.map((e, i) => (
                      <tr key={e.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
                        <td className="px-3 py-2 text-xs text-[#1a1a1a]">{e.holder_name}</td>
                        <td className="px-3 py-2 text-xs text-[#4a4a4a]">{HOLDER_TYPE_LABELS[e.holder_type] || e.holder_type}</td>
                        <td className="px-3 py-2 text-xs text-[#4a4a4a]">{SECURITY_TYPE_LABELS[e.security_type] || e.security_type}</td>
                        <td className="px-3 py-2 text-xs text-[#1a1a1a]">{e.shares?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-xs text-[#4a4a4a]">{e.ownership_percentage?.toFixed(2)}%</td>
                        <td className="px-3 py-2 text-xs text-[#4a4a4a]">{e.fully_diluted_percentage?.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Documents Section ──────────────────────────────────────────── */}
      <Card className="bg-white border-[#e5e5e5] mb-6">
        <CardHeader className="pb-3 border-b border-[#e5e5e5]">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2 text-[#1a1a1a]">
                <FileText className="w-4 h-4 text-[#ffd87a]" /> Deal Documents
                <span className="text-xs font-normal text-[#4a4a4a]">({documents.length})</span>
              </CardTitle>
              <p className="text-[11px] text-[#a8a8a8] mt-0.5 ml-6">Workspace ID: {dealId}</p>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <label className={`cursor-pointer flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[#e5e5e5] hover:bg-[#f9f9f9] transition-colors ${uploadingFile ? 'opacity-50 pointer-events-none' : ''}`}>
                  {uploadingFile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  Upload File
                  <input type="file" accept={ACCEPT_TYPES} className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
                </label>
                <Button size="sm" variant="outline" className="text-xs border-[#e5e5e5]" onClick={() => setAddingLink(v => !v)}>
                  <Link2 className="w-3.5 h-3.5 mr-1" /> Add Link
                </Button>
              </div>
            )}
          </div>

          {/* Add link form */}
          {addingLink && (
            <div className="mt-3 p-3 bg-[#f9f9f9] rounded-lg flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  value={linkForm.title}
                  onChange={e => setLinkForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Document title"
                  className="flex-1 border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a]"
                />
                <select
                  value={linkForm.type}
                  onChange={e => setLinkForm(f => ({ ...f, type: e.target.value }))}
                  className="border border-[#e5e5e5] rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#ffd87a]"
                >
                  {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <input
                  value={linkForm.url}
                  onChange={e => setLinkForm(f => ({ ...f, url: e.target.value }))}
                  placeholder="https://..."
                  className="flex-1 border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a]"
                />
                <Button size="sm" className="bg-[#ffd87a] hover:bg-[#ffd87a] shrink-0" onClick={handleAddLink}>Add</Button>
                <Button size="sm" variant="outline" className="shrink-0" onClick={() => setAddingLink(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent className="p-4">
          {documents.length === 0 ? (
            <p className="text-sm text-[#4a4a4a] text-center py-4">No documents yet. Upload a PDF, Excel, Word file, or paste a link.</p>
          ) : (
            <div className="space-y-2">
              {documents.map(doc => {
                const DocIcon = docIcon(doc.file_name || '');
                const typeLabel = DOC_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type;
                return (
                  <div key={doc.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#f9f9f9] hover:bg-white border border-transparent hover:border-[#e5e5e5] transition-all">
                    <DocIcon className="w-5 h-5 text-[#4a4a4a] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1a1a1a] truncate">{doc.title}</p>
                      <p className="text-xs text-[#4a4a4a]">{typeLabel}{doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(0)} KB` : ''}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {doc.file_url && (
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 rounded hover:bg-[#e5e5e5] transition-colors">
                          <ExternalLink className="w-4 h-4 text-[#4a4a4a]" />
                        </a>
                      )}
                      {canEdit && (
                        <button onClick={() => handleDeleteDoc(doc.id)}
                          className="p-1.5 rounded hover:bg-red-50 transition-colors">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── IC Memo Section ─────────────────────────────────────────────── */}
      <Card className="bg-white border-[#e5e5e5]">
        <CardHeader className="pb-3 border-b border-[#e5e5e5]">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg flex items-center gap-2 text-[#1a1a1a]">
                <FileText className="w-5 h-5 text-[#ffd87a]" /> Investment Committee Memo
              </CardTitle>
              {memoStatus && (
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[memoStatus] || 'bg-[#f5f5f5] text-[#4a4a4a]'}`}>
                  {memoStatus.replace(/_/g, ' ').toUpperCase()}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Create memo */}
              {!icMemo && canEdit && (
                <Button size="sm" onClick={handleCreateMemo} disabled={creatingMemo} className="bg-[#ffd87a] hover:bg-[#ffd87a]">
                  {creatingMemo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  Create Memo
                </Button>
              )}

              {/* Download PDF */}
              {icMemo && (
                <Button size="sm" variant="outline" onClick={handleDownloadPDF} disabled={downloadingPDF}
                  className="border-[#e5e5e5] text-[#4a4a4a] hover:bg-[#f9f9f9]">
                  {downloadingPDF ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                  PDF
                </Button>
              )}

              {/* AI Generate (analyst, non-approved) */}
              {icMemo && canEdit && !isApproved && (
                <Button size="sm" variant="outline" onClick={handleGenerateAI} disabled={generatingAI}
                  className="border-violet-300 text-violet-700 hover:bg-violet-50">
                  {generatingAI
                    ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Researching…</>
                    : <><Sparkles className="w-4 h-4 mr-1" /> AI Generate</>}
                </Button>
              )}

              {/* Submit for review (analyst) */}
              {icMemo && canSubmitMemo && (
                <Button size="sm" onClick={openReviewerDialog} className="bg-[#ffd87a] hover:bg-[#ffd87a]">
                  <Send className="w-4 h-4 mr-1" /> Submit for Review
                </Button>
              )}

              {/* Reviewer actions */}
              {icMemo && canReviewMemo && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setRevisionDialog(true)}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50">
                    <RotateCcw className="w-4 h-4 mr-1" /> Request Revision
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRejectDialog(true)}
                    className="border-red-300 text-red-600 hover:bg-red-50">
                    <ThumbsDown className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Button size="sm" onClick={handleApproveMemo} className="bg-[#ffd87a] hover:bg-[#ffd87a]/90 text-[#1a1a1a] font-medium">
                    <ThumbsUp className="w-4 h-4 mr-1" /> Approve
                  </Button>
                </>
              )}

              {/* Generate Summary (CIO/Principal, post-approval) */}
              {icMemo && isApproved && canApprove && (
                <Button size="sm" variant="outline" onClick={handleGenerateSummary} disabled={generatingSummary}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  {generatingSummary
                    ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Generating…</>
                    : <><Sparkles className="w-4 h-4 mr-1" /> Generate Summary</>}
                </Button>
              )}

              {/* Send to External IC */}
              {icMemo?.ic_summary && canApprove && (
                <Button size="sm" onClick={() => setSendICDialog(true)}
                  className="bg-[#ffd87a] hover:bg-[#ffd87a]/90 text-[#1a1a1a] font-medium">
                  <Mail className="w-4 h-4 mr-1" /> Send to External IC
                </Button>
              )}
            </div>
          </div>

          {/* Approved banner */}
          {isApproved && (
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="text-sm font-medium text-emerald-800">
                This IC Memo has been approved
                {icMemo?.approved_at && ` on ${new Date(icMemo.approved_at).toLocaleDateString()}`}
              </span>
              {icMemo?.external_ic_sent_at && (
                <span className="ml-auto text-xs text-emerald-600 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Sent to external IC {new Date(icMemo.external_ic_sent_at).toLocaleDateString()}
                </span>
              )}
            </div>
          )}

          {/* Revision comments banner */}
          {memoStatus === 'revision_requested' && icMemo?.revision_comments && (
            <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800 flex items-start gap-2">
              <MessageSquare className="w-4 h-4 shrink-0 mt-0.5" />
              <div><span className="font-medium">Revision requested: </span>{icMemo.revision_comments}</div>
            </div>
          )}

          {/* Rejection reason banner */}
          {memoStatus === 'rejected' && icMemo?.rejection_reason && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start gap-2">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div><span className="font-medium">Rejected: </span>{icMemo.rejection_reason}</div>
            </div>
          )}
        </CardHeader>

        <CardContent className="p-0">
          {!icMemo ? (
            <div className="text-center py-12">
              <FileText className="w-14 h-14 text-[#4a4a4a] mx-auto mb-3" />
              <p className="text-[#4a4a4a] mb-1">No IC Memo created yet</p>
              <p className="text-xs text-[#4a4a4a]">
                {canEdit ? 'Click "Create Memo" to get started' : 'The analyst will create the memo'}
              </p>
            </div>
          ) : (
            <div className="flex min-h-[480px]">
              {/* Section nav */}
              <nav className="w-52 shrink-0 border-r border-[#e5e5e5] p-3 space-y-0.5">
                {navSections.map(s => {
                  const hasContent = !!icMemo[s.key];
                  const isActive   = activeSection === s.key;
                  const isEditing  = editingSections.has(s.key);
                  return (
                    <button
                      key={s.key}
                      onClick={() => setActiveSection(s.key)}
                      className={`w-full px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between ${
                        isActive ? 'bg-[#f9f9f9] text-[#1a1a1a] font-medium' : 'text-[#4a4a4a] hover:bg-[#f9f9f9]'
                      }`}
                    >
                      <span>{s.label}</span>
                      <div className="flex items-center gap-1">
                        {isEditing && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
                        {hasContent && !isEditing && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Section content */}
              <div className="flex-1 min-w-0 p-6 overflow-auto">
                {navSections.filter(s => s.key === activeSection).map(s => {
                  const isEditing = editingSections.has(s.key);
                  const isSummarySection = s.key === 'ic_summary';

                  return (
                    <div key={s.key}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-base font-semibold text-[#1a1a1a]">{s.label}</h3>
                        {/* Per-section edit controls — not for ic_summary */}
                        {!isSummarySection && canEditMemo && !isEditing && (
                          <Button size="sm" variant="outline" onClick={() => startEdit(s.key)}
                            className="border-[#e5e5e5] text-[#4a4a4a] hover:text-[#1a1a1a]">
                            <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                          </Button>
                        )}
                        {!isSummarySection && isEditing && (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => cancelEdit(s.key)}>
                              <X className="w-3.5 h-3.5 mr-1" /> Cancel
                            </Button>
                            <Button size="sm" onClick={() => saveSection(s.key)}
                              disabled={savingSection === s.key}
                              className="bg-[#ffd87a] hover:bg-[#ffd87a]">
                              {savingSection === s.key
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
                            </Button>
                          </div>
                        )}
                      </div>

                      {isEditing && !isSummarySection ? (
                        <textarea
                          value={sectionDrafts[s.key] || ''}
                          onChange={e => setSectionDrafts(prev => ({ ...prev, [s.key]: e.target.value }))}
                          placeholder={`Enter ${s.label.toLowerCase()}…`}
                          className="w-full h-72 p-3 text-sm border border-[#e5e5e5] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent text-[#1a1a1a] placeholder:text-[#4a4a4a]"
                          autoFocus
                        />
                      ) : isSummarySection ? (
                        <div className="text-sm text-[#4a4a4a] whitespace-pre-wrap leading-relaxed bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                          {icMemo.ic_summary}
                        </div>
                      ) : (
                        <SectionContent sectionKey={s.key} value={icMemo[s.key]} />
                      )}
                    </div>
                  );
                })}

                {/* Metadata footer */}
                <div className="mt-6 pt-4 border-t border-[#e5e5e5] flex items-center gap-6 text-xs text-[#4a4a4a]">
                  <span>Version {icMemo.version || 1}</span>
                  {icMemo.submitted_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Submitted {new Date(icMemo.submitted_at).toLocaleDateString()}
                    </span>
                  )}
                  {icMemo.approved_at && (
                    <span className="flex items-center gap-1 text-emerald-500">
                      <CheckCircle className="w-3 h-3" />
                      Approved {new Date(icMemo.approved_at).toLocaleDateString()}
                    </span>
                  )}
                  {icMemo.external_ic_sent_at && (
                    <span className="flex items-center gap-1 text-blue-500">
                      <Mail className="w-3 h-3" />
                      Sent to external IC {new Date(icMemo.external_ic_sent_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Reviewer selection dialog ─────────────────────────────────── */}
      {reviewerDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl max-h-[85vh] flex flex-col">
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-1 shrink-0">Submit for Review</h3>
            <p className="text-sm text-[#4a4a4a] mb-4 shrink-0">Select who should review and approve this IC Memo.</p>
            {loadingReviewers ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-[#ffd87a]" />
              </div>
            ) : reviewers.length === 0 ? (
              <p className="text-sm text-[#4a4a4a] py-4 text-center">No reviewers found in the organisation.</p>
            ) : (
              <div className="space-y-2 mb-4 overflow-y-auto flex-1 min-h-0">
                {reviewers.map(r => (
                  <label key={r.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedReviewerId === r.id ? 'border-[#ffd87a] bg-[#ffd87a]/10' : 'border-[#e5e5e5] hover:bg-[#f9f9f9]'
                    }`}>
                    <input type="radio" name="reviewer" value={r.id}
                      checked={selectedReviewerId === r.id}
                      onChange={() => setSelectedReviewerId(r.id)}
                      className="accent-[#ffd87a]"
                    />
                    <div>
                      <p className="text-sm font-medium text-[#1a1a1a]">{r.full_name}</p>
                      <p className="text-xs text-[#4a4a4a]">{r.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 shrink-0">
              <Button variant="outline" onClick={() => setReviewerDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmitMemo} disabled={submittingMemo || reviewers.length === 0}
                className="bg-[#ffd87a] hover:bg-[#ffd87a]">
                {submittingMemo ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject dialog ─────────────────────────────────────────────── */}
      {rejectDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-4">Reject IC Memo</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection…"
              className="w-full h-32 p-3 text-sm border border-[#e5e5e5] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-400 mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setRejectDialog(false); setRejectReason(''); }}>Cancel</Button>
              <Button onClick={handleRejectMemo} className="bg-red-600 hover:bg-red-700" disabled={!rejectReason.trim()}>
                Confirm Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Revision dialog ───────────────────────────────────────────── */}
      {revisionDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-4">Request Revision</h3>
            <textarea
              value={revisionComments}
              onChange={e => setRevisionComments(e.target.value)}
              placeholder="Describe what needs to be revised…"
              className="w-full h-32 p-3 text-sm border border-[#e5e5e5] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setRevisionDialog(false); setRevisionComments(''); }}>Cancel</Button>
              <Button onClick={handleRequestRevision} className="bg-orange-500 hover:bg-orange-600" disabled={!revisionComments.trim()}>
                Send Request
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Send to External IC dialog ────────────────────────────────── */}
      {sendICDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl">
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-1">Send to External IC</h3>
            <p className="text-sm text-[#4a4a4a] mb-4">Send the IC summary to external Investment Committee members via email.</p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Recipients <span className="text-red-400">*</span></label>
                <input
                  value={sendRecipients}
                  onChange={e => setSendRecipients(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                  className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Subject (optional)</label>
                <input
                  value={sendSubject}
                  onChange={e => setSendSubject(e.target.value)}
                  placeholder={`IC Memo: ${icMemo?.title || ''}`}
                  className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1 block">Personal note (optional)</label>
                <textarea
                  value={sendMessage}
                  onChange={e => setSendMessage(e.target.value)}
                  placeholder="Add a personal note before the summary…"
                  rows={3}
                  className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#ffd87a]"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSendICDialog(false)}>Cancel</Button>
              <Button onClick={handleSendToExternalIC} disabled={sendingIC} className="bg-[#ffd87a] hover:bg-[#ffd87a]/90 text-[#1a1a1a] font-medium">
                {sendingIC ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}
                Send
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* New Cap Table Snapshot Dialog */}
      {snapshotDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-xl">
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-4">New Cap Table Snapshot</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-xs text-[#4a4a4a]">Snapshot Date</label>
                <input type="date" value={snapshotForm.snapshot_date}
                  onChange={e => setSnapshotForm(f => ({ ...f, snapshot_date: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5] rounded-lg" />
              </div>
              <div>
                <label className="text-xs text-[#4a4a4a]">Total Shares O/S</label>
                <input type="number" value={snapshotForm.total_shares_outstanding}
                  onChange={e => setSnapshotForm(f => ({ ...f, total_shares_outstanding: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5] rounded-lg" />
              </div>
              <div>
                <label className="text-xs text-[#4a4a4a]">Fully Diluted Shares</label>
                <input type="number" value={snapshotForm.fully_diluted_shares}
                  onChange={e => setSnapshotForm(f => ({ ...f, fully_diluted_shares: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5] rounded-lg" />
              </div>
            </div>

            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-[#1a1a1a]">Holders</span>
              <Button variant="outline" size="sm" onClick={() => setSafeDialog(true)} className="text-xs">
                Convert SAFEs into this snapshot
              </Button>
            </div>

            <div className="space-y-2">
              {snapshotForm.entries.map((row, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-1.5 items-center">
                  <input placeholder="Holder name" value={row.holder_name}
                    onChange={e => updateCapTableRow(idx, 'holder_name', e.target.value)}
                    className="col-span-3 px-2 py-1.5 text-xs border border-[#e5e5e5] rounded-lg" />
                  <select value={row.holder_type} onChange={e => updateCapTableRow(idx, 'holder_type', e.target.value)}
                    className="col-span-2 px-1 py-1.5 text-xs border border-[#e5e5e5] rounded-lg">
                    {Object.entries(HOLDER_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <select value={row.security_type} onChange={e => updateCapTableRow(idx, 'security_type', e.target.value)}
                    className="col-span-2 px-1 py-1.5 text-xs border border-[#e5e5e5] rounded-lg">
                    {Object.entries(SECURITY_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <input type="number" placeholder="Shares" value={row.shares}
                    onChange={e => updateCapTableRow(idx, 'shares', e.target.value)}
                    className="col-span-2 px-2 py-1.5 text-xs border border-[#e5e5e5] rounded-lg" />
                  <input type="number" placeholder="Own. %" value={row.ownership_percentage}
                    onChange={e => updateCapTableRow(idx, 'ownership_percentage', e.target.value)}
                    className="col-span-1 px-2 py-1.5 text-xs border border-[#e5e5e5] rounded-lg" />
                  <input type="number" placeholder="FD %" value={row.fully_diluted_percentage}
                    onChange={e => updateCapTableRow(idx, 'fully_diluted_percentage', e.target.value)}
                    className="col-span-1 px-2 py-1.5 text-xs border border-[#e5e5e5] rounded-lg" />
                  <button onClick={() => removeCapTableRow(idx)} className="col-span-1 text-[#c97a3a] hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addCapTableRow} className="mt-2 text-xs w-fit">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add holder
            </Button>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setSnapshotDialog(false)}>Cancel</Button>
              <Button onClick={submitSnapshot} disabled={savingSnapshot} className="bg-[#ffd87a] hover:bg-[#ffd87a]">
                {savingSnapshot ? 'Saving…' : 'Save Snapshot'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Convert SAFEs Dialog */}
      {safeDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-1">Convert SAFEs / Notes</h3>
            <p className="text-xs text-[#4a4a4a] mb-4">
              Enter the terms of the triggering priced round. Active SAFE and convertible-note
              investments will be converted to shares using the lower of cap price / discount price.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#4a4a4a]">Round Price / Share</label>
                <input type="number" value={safeForm.round_price_per_share}
                  onChange={e => setSafeForm(f => ({ ...f, round_price_per_share: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5] rounded-lg" />
              </div>
              <div>
                <label className="text-xs text-[#4a4a4a]">Pre-Money Valuation</label>
                <input type="number" value={safeForm.pre_money_valuation}
                  onChange={e => setSafeForm(f => ({ ...f, pre_money_valuation: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5] rounded-lg" />
              </div>
              <div>
                <label className="text-xs text-[#4a4a4a]">Fully Diluted Shares (pre-round)</label>
                <input type="number" value={safeForm.fully_diluted_shares_pre_round}
                  onChange={e => setSafeForm(f => ({ ...f, fully_diluted_shares_pre_round: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5] rounded-lg" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setSafeDialog(false)}>Cancel</Button>
              <Button onClick={runConvertSafes} disabled={convertingSafes} className="bg-[#ffd87a] hover:bg-[#ffd87a]">
                {convertingSafes ? 'Converting…' : 'Convert'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Liquidation Waterfall Dialog */}
      {waterfallDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => { setWaterfallDialog(false); setWaterfallResult(null); }}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-1">Liquidation Waterfall</h3>
            <p className="text-xs text-[#4a4a4a] mb-4">
              MVP model: preferred/SAFE/convertible holders are paid 1× non-participating preference pari passu;
              common/options share the residual pro-rata by fully-diluted %.
            </p>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-xs text-[#4a4a4a]">Exit Value</label>
                <input type="number" value={exitValue} onChange={e => setExitValue(e.target.value)}
                  className="w-full mt-1 px-2 py-1.5 text-sm border border-[#e5e5e5] rounded-lg" />
              </div>
              <Button onClick={runWaterfall} disabled={runningWaterfall} className="bg-[#ffd87a] hover:bg-[#ffd87a]">
                {runningWaterfall ? 'Running…' : 'Run'}
              </Button>
            </div>

            {waterfallResult && (
              <div className="mt-4">
                {waterfallResult.preference_shortfall && (
                  <div className="mb-3 px-3 py-2 text-xs bg-[#fdf1e0] border border-[#e8a838] rounded-lg text-[#8a5a1a]">
                    Exit value is below the total preference stack ({fmtCur(waterfallResult.total_preference_stack, user?.market)}) —
                    preference holders are paid pro-rata and residual holders receive $0.
                  </div>
                )}
                <div className="border border-[#e5e5e5] overflow-hidden rounded-lg">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#dae4c4]">
                        {['Holder', 'Security', 'Preference', 'Payout'].map(h => (
                          <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-[#1a1a1a] uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e5e5e5]">
                      {waterfallResult.payouts.map((p, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
                          <td className="px-3 py-2 text-xs text-[#1a1a1a]">{p.holder_name}</td>
                          <td className="px-3 py-2 text-xs text-[#4a4a4a]">{SECURITY_TYPE_LABELS[p.security_type] || p.security_type}</td>
                          <td className="px-3 py-2 text-xs text-[#4a4a4a]">{p.preference_amount ? fmtCur(p.preference_amount, user?.market) : '—'}</td>
                          <td className="px-3 py-2 text-xs font-medium text-[#1a1a1a]">{fmtCur(p.payout, user?.market)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
